import { Point } from "mapbox-gl";
import { RootStore } from "../../shared/stores/RootStore";
import { nodeReferences } from "../../cra/stores/CraNodeStore";
import { craNodeShiftDragLayerName } from "./CraMapSource";
import { HlaLineProps, hlaWayShiftDragLayerNames } from "./HlaMapSource";
import { BBoxSelection } from "./BBoxSelection";
import { MapMode } from "../../survey/stores/MapStore";
import { mapillaryPointShiftDragLayerName } from "./MapillaryMapSource";

export class ShiftDragSelection {
  map: mapboxgl.Map;
  rootStore: RootStore;
  bboxSelection?: BBoxSelection;

  selectCraNodes = false;
  selectHlaWays = false;
  selectMapillaryPoints = false;

  constructor(map: mapboxgl.Map, rootStore: RootStore) {
    this.map = map;
    this.rootStore = rootStore;
  }

  setSelectCraNodes(value: boolean) {
    this.selectCraNodes = value;
  }

  setSelectHlaWays(value: boolean) {
    this.selectHlaWays = value;
  }

  onModeChange(mode: MapMode) {
    this.disable();
    if (
      mode === MapMode.SurveyPage ||
      mode === MapMode.ManageNodes ||
      mode === MapMode.CraCoding ||
      mode === MapMode.HlaCoding ||
      mode === MapMode.MapillaryImport
    ) {
      this.enable();
    }

    this.selectMapillaryPoints = mode === MapMode.MapillaryImport;

    this.selectHlaWays =
      mode === MapMode.SurveyPage || mode === MapMode.HlaCoding;

    this.selectCraNodes =
      mode === MapMode.SurveyPage ||
      mode === MapMode.ManageNodes ||
      mode === MapMode.CraCoding;
  }

  setSelectMapillaryPoints(value: boolean) {
    this.selectMapillaryPoints = value;
  }

  disable() {
    this.bboxSelection?.disable();
  }

  enable() {
    this.bboxSelection = new BBoxSelection(this.map, {
      onDrag: (bbox) => this.onFinish(bbox),
      onClick: (point) => this.onFinish(point),
    });
    this.bboxSelection.enable();
  }

  onFinish(query: Point | [Point, Point]) {
    const { craWayStore, craNodeStore, hlaStore, mapillaryStore } =
      this.rootStore;

    if (this.selectCraNodes) {
      const selectedWay = craWayStore.getSelected();
      if (selectedWay) {
        const features = this.map.queryRenderedFeatures(query, {
          layers: [craNodeShiftDragLayerName],
        });

        features.forEach((f) => {
          const nodeId = f.properties?.id as string;

          const nodeIsInSelectedWay =
            nodeReferences.get(nodeId)?.craWayId === selectedWay.id;

          if (nodeIsInSelectedWay) {
            craNodeStore.toggleMultiSelectedId(nodeId);
          }
        });
      }
    }

    if (this.selectHlaWays) {
      const features = this.map.queryRenderedFeatures(query, {
        layers: hlaWayShiftDragLayerNames,
      });

      const hlaWayIds = new Set(
        features
          .filter((f) => {
            // don't include ways that were filtered out
            const props = f.properties as HlaLineProps;
            return !props.hideLeftLane || !props.hideRightLane;
          })
          .map((f) => f.properties?.hlaWayId as string),
      );

      hlaWayIds.forEach((hlaWayId) => {
        hlaStore.toggleMultiSelectedId(hlaWayId);
      });
    }

    if (this.selectMapillaryPoints) {
      const selectedSequenceId = mapillaryStore.selectedSequenceId;
      if (!selectedSequenceId) return;

      const features = this.map.queryRenderedFeatures(query, {
        layers: [mapillaryPointShiftDragLayerName],
      });

      const imageIds = new Set<string>();

      features.forEach((f) => {
        const imageId = f.properties?.id as string | undefined;
        const sequenceId = f.properties?.sequenceId as string | undefined;

        if (!imageId) return;
        if (sequenceId !== selectedSequenceId) return;

        imageIds.add(imageId);
      });

      imageIds.forEach((imageId) => {
        mapillaryStore.togglePointSelected(imageId);
      });
    }
  }
}
